export type FormType = 'AAA' | 'ABB' | 'ABA' | 'ABC';

export interface SuperGroup {
  readonly title: string;
  /** URL-safe id used for the rail nav + scroll-spy section anchor. */
  readonly slug: string;
  readonly color: string;
  readonly patterns: readonly string[];
}

export const PATTERN_SUPER_GROUPS: readonly SuperGroup[] = [
  {
    title: 'Без зміни',
    slug: 'g-no-change',
    color: '#7A7F88',
    patterns: ['no-change'],
  },
  {
    title: 'Заміна закінчення',
    slug: 'g-ending',
    color: '#488BFF',
    patterns: [
      'd-to-t',
      'ought',
      'aught',
      'eep-to-ept',
      'eel-eal-to-elt',
      'ay-to-aid',
      'ind-to-ound',
      'shorten',
      'ell-to-old',
      'and-to-ood',
      'old-to-eld',
      'it-to-at',
      'et-ot-otten',
    ],
  },
  {
    title: 'Зміна голосної + -en',
    slug: 'g-vowel',
    color: '#9B7CFF',
    patterns: [
      'i-a-u',
      'i-u-u',
      'ake-ook-aken',
      'oke-oken',
      'ide-ode-idden',
      'ive-ove-iven',
      'ear-ore-orn',
      'aw-ew-awn',
      'ow-ew-own',
      'ow-owed-own',
      'ee-aw-een',
      'ise-ose-isen',
      'ay-ain',
      'ive-ave-iven',
      'weave-pattern',
    ],
  },
  {
    title: 'Спец-сімейства',
    slug: 'g-special',
    color: '#FF7043',
    patterns: [
      'do-family',
      'come-family',
      'run-family',
      'hear-family',
      'make-family',
      'shoot-family',
      'suppletive',
      'unique',
    ],
  },
];

const PATTERN_LABELS: Record<string, string> = {
  'no-change': 'Без зміни (V1=V2=V3)',
  'd-to-t': 'Заміна -d → -t',
  'ought': 'Закінчення -ought',
  'aught': 'Закінчення -aught',
  'eep-to-ept': '-eep → -ept',
  'eel-eal-to-elt': '-eel/-eal → -elt',
  'ay-to-aid': '-ay → -aid',
  'ind-to-ound': '-ind → -ound',
  'shorten': 'Укорочення голосної (-eed/-ead → -ed)',
  'ell-to-old': '-ell → -old',
  'and-to-ood': '-and → -ood',
  'old-to-eld': '-old → -eld',
  'it-to-at': '-it → -at',
  'et-ot-otten': '-et → -ot → -otten',
  'i-a-u': 'Зміна i → a → u',
  'i-u-u': 'Зміна i → u → u',
  'ake-ook-aken': '-ake → -ook → -aken',
  'oke-oken': '-oke → -oken',
  'ide-ode-idden': '-ide → -ode → -idden',
  'ive-ove-iven': '-ive → -ove → -iven',
  'ear-ore-orn': '-ear → -ore → -orn',
  'aw-ew-awn': '-aw → -ew → -awn (draw)',
  'ow-ew-own': '-ow → -ew → -own (blow, fly)',
  'ow-owed-own': '-ow → -owed → -own (sow, mow)',
  'ee-aw-een': '-ee → -aw → -een (see)',
  'ise-ose-isen': '-ise → -ose → -isen (rise)',
  'ay-ain': '-ay → -ain (lie)',
  'ive-ave-iven': '-ive → -ave → -iven (give)',
  'weave-pattern': 'weave/wove/woven',
  'do-family': 'do/did/done + похідні',
  'come-family': 'come/came/come + похідні',
  'run-family': 'run/ran/run + похідні',
  'hear-family': 'hear/heard/heard + похідні',
  'make-family': 'make/made/made + похідні',
  'shoot-family': 'shoot/shot/shot + похідні',
  'suppletive': 'Корінь міняється (be, go)',
  'unique': 'Унікальні (have, eat, fall ...)',
};

const FORM_DESCRIPTIONS: Record<FormType, string> = {
  AAA: 'Всі три форми однакові',
  ABB: 'Past Simple = Past Participle',
  ABA: 'Інфінітив = Past Participle',
  ABC: 'Всі три форми різні',
};

const PATTERN_EXAMPLES: Record<string, string> = {
  'no-change': 'cut · put · hit',
  'd-to-t': 'build → built',
  'ought': 'buy → bought',
  'aught': 'teach → taught',
  'eep-to-ept': 'keep → kept',
  'eel-eal-to-elt': 'feel → felt',
  'ay-to-aid': 'pay → paid',
  'ind-to-ound': 'find → found',
  'shorten': 'feed → fed',
  'ell-to-old': 'sell → sold',
  'and-to-ood': 'stand → stood',
  'old-to-eld': 'hold → held',
  'it-to-at': 'sit → sat',
  'et-ot-otten': 'get → got → gotten',
  'i-a-u': 'sing → sang → sung',
  'i-u-u': 'dig → dug → dug',
  'ake-ook-aken': 'take → took → taken',
  'oke-oken': 'speak → spoke → spoken',
  'ide-ode-idden': 'ride → rode → ridden',
  'ive-ove-iven': 'drive → drove → driven',
  'ear-ore-orn': 'wear → wore → worn',
  'aw-ew-awn': 'draw → drew → drawn',
  'ow-ew-own': 'fly → flew → flown',
  'ow-owed-own': 'sow → sowed → sown',
  'ee-aw-een': 'see → saw → seen',
  'ise-ose-isen': 'rise → rose → risen',
  'ay-ain': 'lie → lay → lain',
  'ive-ave-iven': 'give → gave → given',
  'weave-pattern': 'weave → wove → woven',
  'do-family': 'do → did → done',
  'come-family': 'come → came → come',
  'run-family': 'run → ran → run',
  'hear-family': 'hear → heard → heard',
  'make-family': 'make → made → made',
  'shoot-family': 'shoot → shot → shot',
  'suppletive': 'go → went → gone',
  'unique': 'be · have · eat',
};

export function getPatternExample(pattern: string): string {
  return PATTERN_EXAMPLES[pattern] ?? '';
}

export const LEVEL_COLORS: Record<string, string> = {
  A1: '#27CCBC',
  A2: '#3FA98C',
  B1: '#488BFF',
  B2: '#5B5BD6',
  C1: '#3B2C93',
  C2: '#2A2540',
};

export interface LevelMeta {
  readonly key: string;
  readonly name: string;
}

export const LEVELS: readonly LevelMeta[] = [
  { key: 'A1', name: 'Початковий' },
  { key: 'A2', name: 'Елементарний' },
  { key: 'B1', name: 'Середній' },
  { key: 'B2', name: 'Вище середнього' },
  { key: 'C1', name: 'Просунутий' },
  { key: 'C2', name: 'Вільний' },
];

export function getLevelColor(level: string): string {
  return LEVEL_COLORS[level] ?? '#7A7F88';
}

// Ukrainian noun-form helper: "дієслово / дієслова / дієслів" based on count.
export function dieslova(n: number): string {
  const d = n % 10;
  const h = n % 100;
  if (d === 1 && h !== 11) return 'дієслово';
  if (d >= 2 && d <= 4 && (h < 12 || h > 14)) return 'дієслова';
  return 'дієслів';
}

export function rgba(hex: string, a: number): string {
  const h = hex.replace('#', '');
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${a})`;
}

export function getSuperGroupForPattern(pattern: string): string | null {
  for (const group of PATTERN_SUPER_GROUPS) {
    if (group.patterns.includes(pattern)) {
      return group.title;
    }
  }
  return null;
}

export function getPatternLabel(pattern: string): string {
  return PATTERN_LABELS[pattern] ?? pattern;
}

export function getFormLabel(form: FormType): string {
  return form;
}

export function getFormDescription(form: FormType): string {
  return FORM_DESCRIPTIONS[form];
}
